// src/components/AdvancedSearchInterface.js
/**
 * Advanced Search Interface Component - Simplified and Optimized
 * Natural language search with intelligent filtering and recommendations
 */
import React, { useRef, useCallback } from "react";
import { useRestaurant } from "../contexts/RestaurantContext";
import { useAdvancedSearch } from "../hooks/useAdvancedSearch";
import { useSearchSuggestions } from "../hooks/useSearchSuggestions";
import AdvancedFilters from "./AdvancedFilters";
import PropTypes from "prop-types";
import "./AdvancedSearchInterface.css";

const AdvancedSearchInterface = ({
  inventory = [],
  onResultSelect = null,
  compact = false,
  showSuggestions = true,
  maxResults = 10,
}) => {
  const { currentRestaurant } = useRestaurant();
  const searchInputRef = useRef(null);

  // Custom hooks for cleaner state management
  const {
    query,
    results,
    loading,
    error,
    selectedResult,
    filters,
    performSearch,
    handleFilterChange,
    clearFilters,
    selectResult,
    setQuery,
    getFilterCount,
  } = useAdvancedSearch(inventory, maxResults);

  const { suggestions, searchHistory, addToHistory } = useSearchSuggestions(
    inventory,
    showSuggestions
  );

  // Local state for filter visibility
  // const [showAdvancedFilters, setShowAdvancedFilters] = React.useState(false);

  // Optimized search handler
  const handleSearch = useCallback(
    async (searchQuery = query) => {
      try {
        const results = await performSearch(searchQuery, currentRestaurant);
        if (results) {
          addToHistory(searchQuery, results.results?.length || 0);
        }
      } catch (error) {
        console.error("Search failed:", error);
      }
    },
    [query, performSearch, currentRestaurant, addToHistory]
  );

  // Result selection handler
  const handleResultClick = useCallback(
    (result) => {
      selectResult(result);
      if (onResultSelect) {
        onResultSelect(result);
      }
    },
    [selectResult, onResultSelect]
  );

  // Suggestion click handler
  const handleSuggestionClick = useCallback(
    (suggestion) => {
      setQuery(suggestion);
      searchInputRef.current?.focus();
    },
    [setQuery]
  );

  // Keyboard event handler for accessibility
  const handleKeyPress = useCallback((e, action) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      action();
    }
  }, []);

  return (
    <div className={`advanced-search-interface ${compact ? "compact" : ""}`}>
      {/* Search Input */}
      <div className="search-input-container">
        <input
          ref={searchInputRef}
          type="text"
          className="search-input"
          placeholder="Ask me anything about our beverages..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
          aria-label="Search beverages"
        />
        <button
          className="search-button"
          onClick={() => handleSearch()}
          disabled={loading}
          aria-label={loading ? "Searching..." : "Search"}
        >
          {loading ? "Searching..." : "Search"}
        </button>
      </div>

      {/* Advanced Filters */}
      <AdvancedFilters
        filters={filters}
        showAdvancedFilters={true}
        onFilterChange={handleFilterChange}
        onClear={clearFilters}
        onApply={() => handleSearch()}
        onToggle={() => {}} // Not needed in simplified version
        getFilterCount={getFilterCount}
      />

      {/* Search Suggestions */}
      <SearchSuggestions
        show={showSuggestions && !results && suggestions.length > 0}
        suggestions={suggestions}
        onSuggestionClick={handleSuggestionClick}
        onKeyPress={handleKeyPress}
      />

      {/* Search History */}
      <SearchHistory
        show={searchHistory.length > 0 && !results}
        history={searchHistory}
        onHistoryClick={handleSearch}
        onKeyPress={handleKeyPress}
      />

      {/* Error State */}
      <ErrorState show={!!error} error={error} onRetry={() => handleSearch()} />

      {/* Search Results */}
      <SearchResults
        show={!!results}
        results={results}
        query={query}
        selectedResult={selectedResult}
        onResultClick={handleResultClick}
        onKeyPress={handleKeyPress}
      />
    </div>
  );
};

// Sub-components for better organization and reduced complexity

const SearchSuggestions = ({
  show,
  suggestions,
  onSuggestionClick,
  onKeyPress,
}) => {
  if (!show) return null;

  return (
    <div className="search-suggestions">
      <h4>Try searching for:</h4>
      <div className="suggestion-chips">
        {suggestions.map((suggestion) => (
          <button
            key={suggestion}
            className="suggestion-chip"
            onClick={() => onSuggestionClick(suggestion)}
            onKeyPress={(e) =>
              onKeyPress(e, () => onSuggestionClick(suggestion))
            }
          >
            {suggestion}
          </button>
        ))}
      </div>
    </div>
  );
};

const SearchHistory = ({ show, history, onHistoryClick, onKeyPress }) => {
  if (!show) return null;

  return (
    <div className="search-history">
      <h4>Recent Searches:</h4>
      <div className="history-items">
        {history.map((item) => (
          <button
            key={`${item.query}-${item.timestamp}`}
            className="history-item"
            onClick={() => onHistoryClick(item.query)}
            onKeyPress={(e) => onKeyPress(e, () => onHistoryClick(item.query))}
          >
            <span className="history-query">{item.query}</span>
            <span className="history-count">{item.resultCount} results</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const ErrorState = ({ show, error, onRetry }) => {
  if (!show) return null;

  return (
    <div className="search-error">
      <p>{error}</p>
      <button onClick={onRetry} className="retry-button">
        Try Again
      </button>
    </div>
  );
};

const SearchResults = ({
  show,
  results,
  query,
  selectedResult,
  onResultClick,
  onKeyPress,
}) => {
  if (!show) return null;

  return (
    <div className="search-results">
      {results.explanation && (
        <div className="search-explanation">
          <p>{results.explanation}</p>
        </div>
      )}

      {results.results && results.results.length > 0 ? (
        <div className="results-list">
          <h4>Found {results.results.length} matches</h4>
          {results.results.map((result, index) => (
            <button
              key={result.id || index}
              className={`result-item ${
                selectedResult?.id === result.id ? "selected" : ""
              }`}
              onClick={() => onResultClick(result)}
              onKeyPress={(e) => onKeyPress(e, () => onResultClick(result))}
            >
              <div className="result-header">
                <h5>{result.name}</h5>
                <span className="result-price">${result.price}</span>
              </div>
              <p className="result-category">{result.category}</p>
              {result.description && (
                <p className="result-description">{result.description}</p>
              )}
              {result.matchReason && (
                <p className="match-reason">
                  <strong>Why this matches:</strong> {result.matchReason}
                </p>
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="no-results">
          <p>
            No matches found for "{query}". Try adjusting your search or
            filters.
          </p>
        </div>
      )}
    </div>
  );
};

// PropTypes for all components
AdvancedSearchInterface.propTypes = {
  inventory: PropTypes.array,
  onResultSelect: PropTypes.func,
  compact: PropTypes.bool,
  showSuggestions: PropTypes.bool,
  maxResults: PropTypes.number,
};

SearchSuggestions.propTypes = {
  show: PropTypes.bool.isRequired,
  suggestions: PropTypes.array.isRequired,
  onSuggestionClick: PropTypes.func.isRequired,
  onKeyPress: PropTypes.func.isRequired,
};

SearchHistory.propTypes = {
  show: PropTypes.bool.isRequired,
  history: PropTypes.array.isRequired,
  onHistoryClick: PropTypes.func.isRequired,
  onKeyPress: PropTypes.func.isRequired,
};

ErrorState.propTypes = {
  show: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onRetry: PropTypes.func.isRequired,
};

SearchResults.propTypes = {
  show: PropTypes.bool.isRequired,
  results: PropTypes.object,
  query: PropTypes.string.isRequired,
  selectedResult: PropTypes.object,
  onResultClick: PropTypes.func.isRequired,
  onKeyPress: PropTypes.func.isRequired,
};

export default AdvancedSearchInterface;
